import { useCallback, useEffect, useRef } from "react";
import type { GuestCheckoutInfo } from "@/lib/guest-checkout-api";
import type { GuestCheckoutDetails } from "@workspace/api-client-react";
import { CheckoutContractChangedError } from "@/lib/checkout-payment-contract";

// Stripe can retain a submit handler from the anonymous render. Completion
// must use the current authenticated token and PFC poller, just like preparation.
export function useCheckoutPaymentCompletion(complete: (publicId: string) => Promise<void>) {
  const latest = useRef(complete);
  latest.current = complete;
  return useCallback((publicId: string) => latest.current(publicId), []);
}

/** Compare commercial inputs, never analytics identities or response timing.
 * This stays in memory; no quote/contact fingerprint is logged or persisted. */
export function guestPaymentContract(quote: object | null, terms: object): string {
  const commercial = quote && Object.fromEntries(Object.entries(quote).filter(([key]) =>
    /price|amount|fee|total|deposit|due|tax|rate$|discount|currency|paymentType|capture|stripe/i.test(key)));
  return JSON.stringify({ commercial, terms });
}

type State<T> = {
  flowId: string;
  ready: boolean;
  actorId: string | null;
  anonymous: boolean;
  draft: GuestCheckoutInfo | null;
  contract: string;
  authenticate?: (details: GuestCheckoutDetails) => Promise<{ error: Error | null; authUserId?: string }>;
  prepare: (paymentMethodId?: string) => Promise<T>;
  changedMessage: string;
  pendingMessage: string;
  onGuestReady?: () => void;
};

/** Invoked by the EXISTING card/wallet final-submit pipeline, not by email save.
 * Keep Elements mounted while the genuine SDK/profile and customer quote
 * hydrate, then call the CURRENT authenticated preparation callback only. */
export function useDeferredGuestCheckout<T>(state: State<T>) {
  const live = useRef(state);
  live.current = state;
  const alive = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  return useCallback(async (paymentMethodId?: string): Promise<T> => {
    const initial = live.current;
    if (inFlight.current) throw new Error(initial.pendingMessage);
    inFlight.current = true;
    try {
      if (initial.ready) return await initial.prepare(paymentMethodId);
      const draft = initial.draft;
      if (!initial.anonymous || !draft || draft.claimed || draft.flowId !== initial.flowId || !initial.authenticate) {
        throw new Error(initial.pendingMessage);
      }
      const result = await initial.authenticate({
        flowId: draft.flowId, firstName: draft.firstName, lastName: draft.lastName, email: draft.email,
      });
      if (result.error) throw result.error;
      if (!result.authUserId) throw new Error(initial.pendingMessage);
      initial.onGuestReady?.();
      const deadline = Date.now() + 25_000;
      while (true) {
        const current = live.current;
        if (!alive.current || current.flowId !== initial.flowId ||
            current.contract !== initial.contract ||
            current.actorId && current.actorId !== result.authUserId ||
            !current.draft || current.draft.id !== draft.id) {
          throw new CheckoutContractChangedError(current.changedMessage);
        }
        if (current.ready && current.actorId === result.authUserId) {
          return await current.prepare(paymentMethodId);
        }
        if (Date.now() >= deadline) throw new Error(current.pendingMessage);
        await new Promise(resolve => window.setTimeout(resolve, 50));
      }
    } finally { inFlight.current = false; }
  }, []);
}
